import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Download, Smartphone, Users, Plus, QrCode, 
  CheckCircle, XCircle, Loader2, UserPlus, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PatientCard = ({ patientId, cardNumber, onUpdate }) => {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    fiscal_code: '',
    relationship: 'figlio'
  });
  const [addingMember, setAddingMember] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (cardNumber) {
      loadCardData();
      loadFamilyMembers();
    }
  }, [cardNumber]);

  const loadCardData = async () => {
    try {
      const res = await axios.get(`${API}/api/card/${cardNumber}/generate-image`, { headers });
      setCardData(res.data);
    } catch (err) {
      console.error('Error loading card:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFamilyMembers = async () => {
    try {
      const res = await axios.get(`${API}/api/card/${cardNumber}/family-members`, { headers });
      setFamilyMembers(res.data.family_members || []);
    } catch (err) {
      console.error('Error loading family members:', err);
    }
  };

  const downloadCard = async () => {
    try {
      const response = await fetch(`${API}/api/card/${cardNumber}/download-pass`, { headers });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PrimeVision_Card_${cardNumber}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Card scaricata! Puoi aggiungerla al tuo Wallet.');
    } catch (err) {
      toast.error('Errore nel download della card');
    }
  };

  const addToWallet = (type) => {
    // Per Apple Wallet servirebbe un .pkpass firmato
    // Per Google Wallet servirebbe Google Pay API
    // Per ora scarichiamo l'immagine che può essere salvata manualmente
    toast.info(`Per aggiungere al ${type} Wallet, scarica l'immagine e salvala nella galleria, poi usa "Aggiungi a Wallet" dal tuo telefono.`);
    downloadCard();
  };

  const handleAddMember = async () => {
    if (!newMember.first_name || !newMember.last_name || !newMember.date_of_birth) {
      toast.error('Compila nome, cognome e data di nascita');
      return;
    }

    setAddingMember(true);
    try {
      await axios.post(`${API}/api/card/${cardNumber}/add-family-member`, newMember, { headers });
      toast.success(`${newMember.first_name} aggiunto alla card famiglia!`);
      setShowAddMember(false);
      setNewMember({ first_name: '', last_name: '', date_of_birth: '', fiscal_code: '', relationship: 'figlio' });
      loadFamilyMembers();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'aggiunta del membro');
    } finally {
      setAddingMember(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  if (!cardNumber) {
    return (
      <div className="bg-gray-50 rounded-xl p-6 text-center">
        <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">Nessuna Card associata</p>
        <p className="text-sm text-gray-400 mt-1">La Card verrà creata con l'attivazione di un abbonamento</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Card Preview */}
      <div className="relative">
        {cardData?.image_url && (
          <img 
            src={cardData.image_url} 
            alt="Card PRIME VISION" 
            className="w-full rounded-xl shadow-lg"
          />
        )}
        
        {/* Badge stato */}
        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${
          cardData?.has_subscription 
            ? 'bg-[#2A9FCB] text-white' 
            : 'bg-gray-500 text-white'
        }`}>
          {cardData?.card_type}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#2A9FCB]" />
            <span className="font-mono font-bold text-[#2A9FCB]">{cardNumber}</span>
          </div>
          {cardData?.has_subscription && (
            <span className="text-sm text-[#2A9FCB] font-medium">
              {cardData.plan_name} • Scade: {cardData.expiry_date}
            </span>
          )}
        </div>

        {/* Pulsanti Wallet */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => addToWallet('Apple')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Apple Wallet
          </button>
          <button
            onClick={() => addToWallet('Google')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google Wallet
          </button>
        </div>

        {/* Download diretto */}
        <button
          onClick={downloadCard}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#E6F4FA] text-[#2A9FCB] rounded-lg hover:bg-[#d0ebf5] transition-colors"
        >
          <Download className="w-4 h-4" />
          Scarica Immagine Card
        </button>
      </div>

      {/* Membri Famiglia (solo se abbonamento famiglia) */}
      {cardData?.plan_name === 'Famiglia' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#2A9FCB]" />
              <h4 className="font-semibold text-gray-800">Membri Famiglia</h4>
              <span className="text-sm text-gray-500">({familyMembers.length}/5)</span>
            </div>
            {familyMembers.length < 5 && (
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#2A9FCB] text-white rounded-lg text-sm hover:bg-[#2590BA] transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Aggiungi
              </button>
            )}
          </div>

          {familyMembers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Nessun membro famiglia aggiunto. Puoi aggiungere fino a 5 membri.
            </p>
          ) : (
            <div className="space-y-2">
              {familyMembers.map((member, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-800">{member.patient_name}</p>
                    <p className="text-sm text-gray-500">{member.relationship} • {member.card_number}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${member.is_active ? 'bg-[#E8F4F8] text-[#2A9FCB]' : 'bg-gray-100 text-gray-600'}`}>
                    {member.is_active ? 'Attivo' : 'Inattivo'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Aggiungi Membro */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#2A9FCB]">Aggiungi Membro Famiglia</h3>
              <button onClick={() => setShowAddMember(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={newMember.first_name}
                    onChange={(e) => setNewMember({...newMember, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    value={newMember.last_name}
                    onChange={(e) => setNewMember({...newMember, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita *</label>
                <input
                  type="date"
                  value={newMember.date_of_birth}
                  onChange={(e) => setNewMember({...newMember, date_of_birth: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                <input
                  type="text"
                  value={newMember.fiscal_code}
                  onChange={(e) => setNewMember({...newMember, fiscal_code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  placeholder="Opzionale"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parentela</label>
                <select
                  value={newMember.relationship}
                  onChange={(e) => setNewMember({...newMember, relationship: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                >
                  <option value="coniuge">Coniuge</option>
                  <option value="figlio">Figlio/a</option>
                  <option value="genitore">Genitore</option>
                  <option value="fratello">Fratello/Sorella</option>
                  <option value="altro">Altro</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddMember(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleAddMember}
                disabled={addingMember}
                className="flex-1 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#2590BA] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {addingMember ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Aggiungi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientCard;
